<template>
  <v-container class="pa-0" fluid style="height: 100%; overflow: hidden;">

    <v-fade-transition hide-on-leave>
      <template v-if="loading">
        <v-row align="center" class="fill-height my-12" justify="center">
          <v-progress-circular size="64" indeterminate color="primary"/>
        </v-row>
      </template>
    </v-fade-transition>

    <template v-if="!loading">
      <v-row style="height: 82px; z-index: 1; position: relative; " no-gutters>
        <v-container
            class="rowQuestion chat-input-holder" fluid>
          <v-row justify="center">
            <v-col cols="12">
              <chat-input :loading="sendingMessage"
                          @submit="sendMessage"
                          @start-new-chat="startNewChat"/>
            </v-col>
          </v-row>
        </v-container>
      </v-row>

      <v-row justify="center" no-gutters style="max-height: calc(100% - 82px);overflow: auto;">
        <v-slide-y-transition hide-on-leave>
          <v-col v-if="messages.length > 0" cols="12">

            <template v-for="(message, index) in formattedMessages">
              <div :key="message.index">
                <chat-message-item :message="message" @vote-down="vote" @vote-up="vote"
                                   :loading="message.inflight || false"/>
                <v-divider v-if="index < formattedMessages.length - 1" class="my-0 py-0 rowDivider"/>
              </div>
            </template>
            <div :key="disclaimerMessage.index">
              <chat-message-item :message="disclaimerMessage"/>
            </div>
          </v-col>
        </v-slide-y-transition>
        <v-fade-transition hide-on-leave>
          <v-col v-if="messages.length === 0" class="fill-height" cols="12" md="10">
            <v-row align="center" class="fill-height" justify="center" no-gutters>
              <v-col class="my-7" cols="10">
                <div class="text-h6 accent2--text text-md-center">
                  {{ $t('ask_anything') }}
                </div>
                <div class="text-body-2 text-md-center">
                  {{ $t('ai_disclaimer') }}
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-fade-transition>

        <support-ticket-dialog ref="supportTicketDialog"/>

      </v-row>
    </template>

    <div id="chat-end"/>
  </v-container>
</template>

<script>
import ChatMessageItem from "@/views/EmbedChat/components/ChatMessageItem";
import ChatInput from "@/views/EmbedChat/components/ChatInput";
import chatApi from "@/api/chat-api";
import axios from "axios";
import store from "@/store";
import {v4 as uuidv4} from 'uuid';
import SupportTicketDialog from "@/views/EmbedChat/components/SupportTicketDialog.vue";
import {fetchEventSource} from '@microsoft/fetch-event-source';

// Backendin indeksoidut content_localet. Kiramin sivustot: .fi, .com (en), .de, .fr, .se (sv), .nl.
const SUPPORTED_LOCALES = ['fi', 'en', 'de', 'fr', 'sv', 'nl'];
const LOCALE_ALIASES = {se: 'sv', com: 'en', it: 'en'};

function normalizeLocale(value) {
  if (!value) return null;
  const locale = LOCALE_ALIASES[value] || value;
  return SUPPORTED_LOCALES.includes(locale) ? locale : null;
}

function requestedLanguage(widgetConfig = null) {
  // 1. widget-konfiguraatio, 2. ?language= (iframe-upotus), 3. hostnamen pääte
  if (widgetConfig && widgetConfig.language) {
    return widgetConfig.language;
  }
  if (window.location && window.location.search) {
    const language = new URLSearchParams(window.location.search).get('language');
    if (language) {
      return language;
    }
  }
  const parts = window.location.hostname.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : null;
}

export default {
  name: "EmbedChatView",
  components: {SupportTicketDialog, ChatInput, ChatMessageItem},
  data() {
    return {
      messages: [],
      loading: true,
      sendingMessage: false,
      chatId: null,
      locale: 'fi',
    };
  },
  computed: {
    disclaimerMessage() {
      return {
        id: 'disclaimer_message',
        index: -1,
        message: this.$t('ai_disclaimer'),
        type: 'reply'
      }
    },
    formattedMessages() {
      let index = 0;
      const messages = [...this.messages].reverse();
      return messages.flatMap(m => ([
        {
          ...m,
          id: `${m.id}-a-${uuidv4()}`,
          originalId: m.id,
          chatId: m.chat_id,
          type: 'reply',
          message: m.reply,
          index: index++,
          sourceUrls: m.sourceUrls || [],
          lastUpdatedTimestamp: m.lastUpdatedTimestamp || m.timestamp,
          inflight: m.inflight,
        },
        {
          ...m,
          id: `${m.id}-q-${uuidv4()}`,
          originalId: m.id,
          chatId: m.chat_id,
          type: 'question',
          message: m.message,
          index: index++,
          sourceUrls: [],
          lastUpdatedTimestamp: m.lastUpdatedTimestamp || m.timestamp,
        }]))
    }
  },
  methods: {
    setLocale(language, locale) {
      // UI-käännös sivuston omalla kielellä, jos käännös on (esim. kirami.it → it, vaikka
      // sisältö haetaan englanniksi); muuten content-localen mukaan. Ruotsin avain on 'se'.
      const uiLocale = language === 'com' ? 'en' : language;
      this.$i18n.locale = this.$i18n.messages[uiLocale]
        ? uiLocale
        : (locale === 'sv' ? 'se' : locale);
    },
    async sendMessage(messageText) {
      this.sendingMessage = true;
      if (!this.chatId || this.chatId.startsWith('dummy')) {
        await this.createNewChat(messageText);
      }

      this.$sendAnalyticsEvent('chat-message-sent');

      const uuid = uuidv4();
      const placeholderChatMessage = {
        id: uuid,
        chat_id: this.chatId,
        message: messageText,
        timestamp: new Date(),
        error: false,
        rating: null,
        lastUpdatedTimestamp: new Date().getTime(),
        sourceUrls: [],
        reply: '',
        inflight: true,
      };

      try {
        this.messages.push(placeholderChatMessage);
        const placeholderChatMessageIndex = this.messages.findIndex(m => m.id === uuid)
        const update = (patch) => {
          Object.assign(this.messages[placeholderChatMessageIndex], {
            ...this.messages[placeholderChatMessageIndex],
            ...patch,
            lastUpdatedTimestamp: new Date().getTime(),
          });
        };

        const ctrl = new AbortController();
        const chatMessage = [];
        try {
          await fetchEventSource(chatApi.streamUrl(this.chatId), {
            method: 'POST',
            body: JSON.stringify({
              message: messageText,
              locale: this.locale,
            }),
            headers: {
              'accept-encoding': '',
              'Content-Type': 'application/json',
              'x-api-key': axios.defaults.headers.common['x-api-key'],
              'Accept': 'text/event-stream'
            },
            signal: ctrl.signal,
            openWhenHidden: true,
            onmessage: (ev) => {
              const msg = ev.data.replaceAll('___|n|___', '\n')
              // ev.event: id | message | source_url | error (progressing_event/debug ohitetaan)
              if (ev.event === 'id') {
                update({id: msg});
              } else if (ev.event === 'source_url') {
                const sourceUrls = this.messages[placeholderChatMessageIndex].sourceUrls || [];
                if (!sourceUrls.includes(msg)) {
                  update({sourceUrls: [...sourceUrls, msg]});
                }
              } else if (ev.event === 'error') {
                update({error: true});
              } else if (ev.event === 'message') {
                chatMessage.push(msg);
                update({reply: chatMessage.join("")});
              }
            }
          })
        } catch (e) {
          console.error(e)
        } finally {
          ctrl.abort();
        }
        update({inflight: false});
      } catch (error) {
        console.log(error);
        const messageIndex = this.messages.findIndex(m => m.id === uuid);
        if (messageIndex > -1) {
          placeholderChatMessage.error = true;
          Object.assign(this.messages[messageIndex], placeholderChatMessage);
        }
      }
      this.sendingMessage = false;
    },
    scrollToEnd() {
      this.$nextTick(() => {
        this.$vuetify.goTo('#chat-end', {duration: 0});
      })
    },
    startNewChat() {
      // Chat-id luodaan backendissä vasta ensimmäisellä viestillä
      this.chatId = `dummy-${new Date().getTime()}`;
      this.messages = [];
    },
    async createNewChat(messageText) {
      try {
        const chat = await chatApi.newChat({name: messageText, locale: this.locale});
        this.chatId = chat.id;
      } catch (error) {
        alert(error);
      }
    },
    async vote(message, vote) {
      const newRating = message.rating !== vote ? vote : 0;
      try {
        await chatApi.rating(message, newRating);
        const messages = [...this.messages];
        const index = messages.findIndex(m => m.id === message.originalId);
        if (index > -1) {
          messages[index] = {...messages[index], rating: newRating};
          this.messages = messages;
        }
        if (newRating === -1) {
          this.$refs.supportTicketDialog.open(message);
        }
      } catch (e) {
        console.error(e);
        alert(e);
      }
    },
  },
  async mounted() {
    // Widget-konfiguraatio root-instanssista (jos upotettu widgetinä)
    const widgetConfig = this.$root.widgetConfig || null;

    // x-api-key on istunnon tunniste: widget-konfiguraation apiKey tai selaimen sessio-uuid
    if (widgetConfig && widgetConfig.apiKey) {
      axios.defaults.headers.common['x-api-key'] = widgetConfig.apiKey;
    } else {
      axios.defaults.headers.common['x-api-key'] = store.state.sessionId;
    }

    const language = requestedLanguage(widgetConfig);
    this.locale = normalizeLocale(language) || 'fi';
    this.setLocale(language, this.locale);
    this.startNewChat();
    this.loading = false;

    this.$nextTick(() => {
      // Hakukysely widget-konfiguraatiosta tai URL-parametrista (sivuston hakukenttä)
      let searchQuery = null;
      if (widgetConfig && widgetConfig.searchQuery) {
        searchQuery = widgetConfig.searchQuery;
      } else if (window.location.search) {
        searchQuery = new URLSearchParams(window.location.search).get('search_api_fulltext');
      }
      if (searchQuery) {
        this.sendMessage(`${this.$t('searching_information_about_subject')}: ${searchQuery}`);
      }
    });
  }
}
</script>

<style lang="scss">
.chat-input-holder {
  -webkit-box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
  -moz-box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
  box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
}

#chat-end {
  position: absolute;
  bottom: 0;
}
</style>
